import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { DrawerFooter } from '../../../../web/components-v2/Drawer';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { LeadRuleViewModel } from '../../../viewModels/leadrules';
import { styleSheet } from '../styles';
import { minDaysOptions, ownershipEnforcementOptions, sortCriteriaOptions } from '../utils';
import { DrawerSection } from './DrawerSection';
import { OptionSelect } from './OptionSelect';
import { generateCustomValueOption, getSelectedOption } from './RuleContainerHeader';
import { SetCustomValueModal } from './SetCustomValueModal';

interface IProps {
	leadRule: LeadRuleViewModel;
	onClickToSave: (values: {
		minDays: number;
		ownership: string;
		preserve: boolean | null;
		primarySortCriteria: 'LastInteraction' | 'Random';
		priority: number;
	}) => void;
}

export const AdditionalSettings: React.FC<IProps> = observer(({ leadRule, onClickToSave }) => {
	const defaultMinDays = minDaysOptions[0];
	const defaultOwnership = ownershipEnforcementOptions[1];
	const defaultSortCriteria = sortCriteriaOptions[0];

	const [ownership, setOwnership] = useState(defaultOwnership);
	const [preserve, setPreserve] = useState<boolean>(false);
	const [priority, setPriority] = useState(10);
	const [sortCriteria, setSortCriteria] = useState(defaultSortCriteria);

	const [minDays, setMinDays] = useState(defaultMinDays);

	useEffect(() => {
		if (leadRule) {
			const getOwnership = ownershipEnforcementOptions.filter(
				item => item.dataContext === leadRule?.ownershipEnforcement
			)?.[0];
			setOwnership(getOwnership || defaultOwnership);

			setPreserve(leadRule?.preserve === true);

			setPriority(leadRule?.priority);

			setMinDays(getSelectedOption(leadRule?.minDaysSinceLastInteraction, minDaysOptions));

			const getSortCriteria = sortCriteriaOptions.find(item => item.dataContext === leadRule?.primarySortCriteria);
			setSortCriteria(getSortCriteria || defaultSortCriteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadRule]);

	const handlePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const parsed = parseInt(event.target.value, 10);
		if (parsed && !isNaN(parsed)) {
			setPriority(parsed);
		} else if (!event.target.value) {
			setPriority(null);
		}
	};

	const handleOwnershipChange = (option: ISelectOption) => {
		setOwnership(option);
		leadRule?.setOwnershipEnforcement(option.dataContext);
	};

	const handleMinDaysChange = (option: ISelectOption) => {
		if (option.id !== 'custom') {
			leadRule?.setMinDaysSinceLastInteraction(option.dataContext);
			setMinDays(option);
			return;
		}
		setMinDays(option);
	};

	const handlePreserveChange = (checked: boolean) => {
		setPreserve(checked);
		leadRule?.setPreserve(checked ? true : false);
	};

	const handleSortCriteriaChange = (option: ISelectOption) => {
		setSortCriteria(option);
		leadRule?.setPrimarySortCriteria(option.dataContext);
	};

	const handleSave = () => {
		leadRule?.setPriority(priority);
		onClickToSave({
			minDays: minDays?.dataContext,
			ownership: ownership?.dataContext,
			preserve: preserve ? true : false,
			primarySortCriteria: sortCriteria?.dataContext,
			priority,
		});
	};

	return (
		<>
			<DrawerSection title='Settings'>
				<div className={css(styleSheet.fieldGrid)}>
					<Field styles={[styleSheet.field]}>
						<FieldLabel htmlFor='option-priority-input'>Priority</FieldLabel>
						<Input id='option-priority-input' onChange={handlePriorityChange} type='text' value={priority ?? ''} />
					</Field>
					<Field styles={[styleSheet.field]}>
						<FieldLabel htmlFor='option-ownership'>Ownership</FieldLabel>
						<OptionSelect
							id='option-ownership'
							options={ownershipEnforcementOptions}
							selected={ownership}
							onSelect={handleOwnershipChange}
						/>
					</Field>
					<Field styles={[styleSheet.field]}>
						<FieldLabel htmlFor='option-min-days'>Min days</FieldLabel>
						<OptionSelect
							id='option-min-days'
							options={minDaysOptions}
							selected={minDays}
							onSelect={handleMinDaysChange}
						/>
						{minDays?.id === 'custom' ? (
							<SetCustomValueModal
								onClickToClose={() =>
									setMinDays(getSelectedOption(leadRule?.minDaysSinceLastInteraction, minDaysOptions))
								}
								onClickToSave={value => {
									setMinDays(generateCustomValueOption(Number(value)));
									leadRule.setMinDaysSinceLastInteraction(Number(value));
								}}
							/>
						) : null}
					</Field>
					<Field styles={[styleSheet.field]}>
						<FieldLabel htmlFor='option-sort'>Sort</FieldLabel>
						<OptionSelect
							id='option-sort'
							options={sortCriteriaOptions}
							selected={sortCriteria}
							onSelect={handleSortCriteriaChange}
						/>
					</Field>
				</div>
				<Label htmlFor='option-preserve'>
					<Checkbox id='option-preserve' checked={preserve} onCheckedChange={handlePreserveChange} />
					Save as template
				</Label>
			</DrawerSection>
			<DrawerFooter styles={[styleSheet.footer]}>
				<Button onClick={handleSave}>Save</Button>
			</DrawerFooter>
		</>
	);
});
